import AdminLayout from '../components/AdminLayout'
import AdminStatsRow from '../components/AdminStatsRow'
import { AdminTable, Column } from '../components/AdminTable'
import AdminDateRangePicker, { IsoRange } from '../components/AdminDateRangePicker'
import { Filter } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import adminService from '../utils/adminService'
import { ApiError } from '../utils/api'
import { formatAmount } from '../utils/amountFormatter'

type BalanceRow = {
  key: string
  date: string
  service: string
  amount: string
  status: 'Successful' | 'Pending' | 'Failed'
}

type ModuleRevenue = { module: string; amount: number }

type StatusFilter = 'all' | 'successful' | 'pending' | 'failed'

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'successful', label: 'Successful' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
]

export default function AdminFinanceBalancePage() {
  const [balances, setBalances] = useState<BalanceRow[]>([])
  const [stats, setStats] = useState({ total: '£0', monthly: '£0', annual: '£0', potential: '£0' })
  const [modules, setModules] = useState<ModuleRevenue[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [filterOpen, setFilterOpen] = useState(false)
  const [range, setRange] = useState<IsoRange>({})
  const filterRef = useRef<HTMLDivElement>(null)
  const itemsPerPage = 10
  const rangeStart = range.start
  const rangeEnd = range.end

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, statusFilter, rangeStart, rangeEnd])

  useEffect(() => {
    if (!filterOpen) return
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [filterOpen])

  useEffect(() => {
    const loadData = async () => {
      setError(null)
      setLoading(true)
      try {
        const { response } = await adminService.listTransactions({
          page: currentPage,
          limit: itemsPerPage,
          ...(debouncedSearch ? { q: debouncedSearch } : {}),
          ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
          ...(rangeStart ? { start: rangeStart } : {}),
          ...(rangeEnd ? { end: rangeEnd } : {}),
        })
        const data: any = (response as any)?.data || response
        const items = data?.data ?? data?.items ?? []
        const meta = data?.meta ?? {}
        const summary = data?.summary ?? {}

        setBalances(
          items.map((item: any, idx: number) => {
            const amountInPounds = item.amount ? item.amount / 100 : 0
            let status: BalanceRow['status'] = 'Failed'
            if (item.status === 'paid' || item.status === 'completed' || item.status === 'succeeded' || item.status === 'complete') {
              status = 'Successful'
            } else if (item.status === 'pending' || item.status === 'processing') {
              status = 'Pending'
            }

            return {
              key: item._id || item.id || `${idx}`,
              date: item.createdAt
                ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—',
              service: item.planSlug || item.type || '—',
              amount: `£${formatAmount(amountInPounds)}`,
              status,
            }
          })
        )

        setTotalItems(meta.total ?? 0)
        setModules(Array.isArray(data?.moduleBreakdown) ? data.moduleBreakdown : [])
        setStats({
          total: `£${formatAmount(Number(summary.totalEarnings ?? 0))}`,
          monthly: `£${formatAmount(Number(summary.last30Days ?? 0))}`,
          annual: `£${formatAmount(Number(summary.last365Days ?? 0))}`,
          potential: `£${formatAmount(Number(summary.potentialEarnings ?? 0))}`,
        })
      } catch (err) {
        console.error(err)
        setError(err instanceof ApiError ? err.message : 'Unable to load financial balance.')
      } finally {
        setLoading(false)
      }
    }

    void loadData()
  }, [currentPage, debouncedSearch, statusFilter, rangeStart, rangeEnd])

  const columns: Column<BalanceRow>[] = [
    { key: 'date', label: 'Date' },
    { key: 'service', label: 'Service' },
    { key: 'amount', label: 'Amount' },
    {
      key: 'status',
      label: 'Status',
      render: row => {
        const style =
          row.status === 'Successful'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : row.status === 'Pending'
            ? 'bg-amber-50 text-amber-700 border-amber-100'
            : 'bg-red-50 text-red-700 border-red-100'
        return (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold border ${style}`}>
            {row.status}
          </span>
        )
      },
    },
  ]

  return (
    <AdminLayout>
      <div className='px-4 py-4 sm:px-6 sm:py-6 lg:px-10 space-y-5 sm:space-y-6'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h1 className='text-xl sm:text-2xl font-bold text-gray-900'>Financial Balance</h1>
            <p className='text-sm text-gray-500'>Earnings and transactions across BritFiler.</p>
          </div>
          <div className='self-start sm:self-auto'>
            <AdminDateRangePicker onChange={setRange} />
          </div>
        </div>

        <AdminStatsRow
          stats={[
            { label: 'Total Earnings', value: stats.total },
            { label: 'Earnings (Past 30 Days)', value: stats.monthly },
            { label: 'Annual Earning (Past 365 Days)', value: stats.annual },
            { label: 'Potential Earnings (Draft)', value: stats.potential },
          ]}
        />

        {/* Revenue by module */}
        <div className='rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5'>
          <p className='text-sm font-semibold text-gray-800 mb-4'>Revenue by Module</p>
          <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
            {[
              'Corporation Tax',
              'Companies House',
              'Confirmation Statement',
              'Dormant',
              'Self Assessment',
            ].map(name => {
              const found = modules.find(m => m.module === name)
              return (
                <div key={name} className='rounded-xl border border-gray-100 bg-gray-50 p-3 sm:p-4'>
                  <p className='text-xs font-medium text-gray-500 leading-tight min-h-[2rem]'>{name}</p>
                  <p className='mt-1 text-xl sm:text-2xl font-bold text-gray-900'>
                    {loading ? (
                      <span className='inline-block h-6 w-16 rounded bg-gray-200 animate-pulse align-middle' />
                    ) : (
                      `£${formatAmount(found?.amount ?? 0)}`
                    )}
                  </p>
                </div>
              )
            })}
          </div>
        </div>

        {error && (
          <div className='rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700'>
            {error}
          </div>
        )}

        <AdminTable<BalanceRow>
          columns={columns}
          data={balances}
          loading={loading}
          emptyMessage='No transactions found.'
          searchPlaceholder='Search Transactions'
          searchValue={search}
          onSearchChange={setSearch}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          actions={
            <div className='relative' ref={filterRef}>
              <button
                onClick={() => setFilterOpen(o => !o)}
                className='inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50'
              >
                <Filter className='h-4 w-4 text-gray-500' />
                Filter
                {statusFilter !== 'all' && (
                  <span className='ml-1 rounded-full bg-[#ff4f3c] px-2 py-0.5 text-xs font-semibold text-white capitalize'>
                    {statusFilter}
                  </span>
                )}
              </button>
              {filterOpen && (
                <div className='absolute right-0 z-20 mt-2 w-44 rounded-xl border border-gray-200 bg-white p-1 shadow-lg'>
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setStatusFilter(opt.value)
                        setFilterOpen(false)
                      }}
                      className={`w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                        statusFilter === opt.value ? 'bg-gray-50 font-semibold text-gray-900' : 'text-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          }
        />
      </div>
    </AdminLayout>
  )
}
